import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API, formatApiErrorDetail, useAuth } from "../context/AuthContext";
import { GoogleButton } from "./Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Register() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [therapistId, setTherapistId] = useState("");
  const [therapists, setTherapists] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/auth/therapists`)
      .then(({ data }) => setTherapists(data))
      .catch(() => setTherapists([]));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/register`, {
        name,
        email,
        password,
        therapist_id: !inviteCode && therapistId ? therapistId : null,
        invite_code: inviteCode || null,
      });
      setUser(data);
      navigate(data.role === "therapist" ? "/therapist" : "/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950 px-4 py-10">
      <div className="w-full max-w-md" data-testid="register-page">
        <div className="mb-8 text-center">
          <img src="/logo.png" alt="Forum Erkelenz MyTraining Logo" className="mx-auto mb-4 h-16 w-16 object-contain" data-testid="register-logo" />
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-100">
            Konto erstellen
          </h1>
          <p className="mt-2 text-sm text-zinc-400">
            Registriere dich bei Forum Erkelenz MyTraining – als Patient oder Therapeut.
          </p>
        </div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6 shadow-sm sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Max Mustermann"
                data-testid="register-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-email">E-Mail</Label>
              <Input
                id="reg-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@beispiel.de"
                data-testid="register-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-password">Passwort</Label>
              <Input
                id="reg-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mindestens 6 Zeichen"
                data-testid="register-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-code">
                Einladungscode <span className="font-normal text-zinc-500">(nur für Therapeuten)</span>
              </Label>
              <Input
                id="invite-code"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="Leer lassen für Patienten-Konto"
                data-testid="register-invite-code-input"
              />
            </div>
            {!inviteCode && therapists.length > 0 && (
              <div className="space-y-2">
                <Label>Dein Therapeut (optional)</Label>
                <Select value={therapistId} onValueChange={setTherapistId}>
                  <SelectTrigger data-testid="register-therapist-select">
                    <SelectValue placeholder="Therapeut auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {therapists.map((t) => (
                      <SelectItem
                        key={t.user_id}
                        value={t.user_id}
                        data-testid={`therapist-option-${t.user_id}`}
                      >
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {error && (
              <p
                className="rounded-lg bg-red-950/50 px-3 py-2 text-sm text-destructive"
                data-testid="register-error-message"
              >
                {error}
              </p>
            )}
            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={loading}
              data-testid="register-submit-button"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Registrieren
            </Button>
          </form>
          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-zinc-800" />
            <span className="text-xs uppercase tracking-wider text-zinc-500">oder</span>
            <div className="h-px flex-1 bg-zinc-800" />
          </div>
          <GoogleButton />
        </div>
        <p className="mt-6 text-center text-sm text-zinc-400">
          Bereits registriert?{" "}
          <Link
            to="/login"
            className="font-semibold text-primary hover:underline"
            data-testid="login-link"
          >
            Zum Login
          </Link>
        </p>
      </div>
    </div>
  );
}
